import {
  componentNameValidationMixin,
  portalMixin,
} from '@descope-ui/common/components-mixins';
import { compose } from '@descope-ui/common/utils';
import {
  forwardAttrs,
  getComponentName,
} from '@descope-ui/common/components-helpers';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { EnrichedTextClass } from '@descope-ui/descope-enriched-text/class';

export const componentName = getComponentName('tooltip');

const tooltipAttrs = [
  'text',
  'position',
  'hide-delay',
  'hover-delay',
  'opened',
];

const BaseClass = createBaseClass({
  componentName,
  baseSelector: 'vaadin-tooltip',
});

class RawTooltip extends BaseClass {
  static get observedAttributes() {
    return tooltipAttrs.concat(BaseClass.observedAttributes || []);
  }

  get isOpened() {
    return this.getAttribute('opened') === 'true';
  }

  get overlay() {
    return this.tooltip?._overlayElement;
  }

  get overlayContentNode() {
    return this.overlay?.shadowRoot.querySelector('[part="content"]');
  }

  get tooltipText() {
    return this.getAttribute('text')?.trim() || '';
  }

  get isStaticDisplay() {
    return this.getAttribute('static-display') === 'true';
  }

  // We use `static-display` for presentation purposes, to show the tooltip content.
  // This should be used only when `opened` is `true`. Once `static-display` is set,
  // the overlay would become a `static` element, and will have layout in the presenting page.
  // This is mainly aimed to solve the presentation problem on our Styles Page in the Console.
  #handleStaticDisplay() {
    if (this.isStaticDisplay) {
      this.#revealWrappedParts();
      this.setAttribute('inert', 'true');
    } else {
      this.#hideWrappedParts();
      this.removeAttribute('inert');
    }
  }

  init() {
    super.init();

    // Create the vaadin-tooltip here instead of constructor (for React compatibility)
    this.style.display = 'contents';
    this.insertAdjacentHTML('beforeend', '<vaadin-tooltip></vaadin-tooltip>');
    this.tooltip = this.querySelector('vaadin-tooltip');

    this.#hideWrappedParts();

    this.#setTooltipTarget();

    setTimeout(() => this.#onOverlayReady());
  }

  #hideWrappedParts() {
    this.tooltip.style.width = '0';
    this.tooltip.style.height = '0';
    this.tooltip.style.display = 'block';
    this.tooltip.style.overflow = 'hidden';
    this.tooltip.style.position = 'absolute';
  }
  
  #revealWrappedParts() {
    this.tooltip.style.width = '100%';
    this.tooltip.style.height = '100%';
    this.tooltip.style.position = 'static';
    this.tooltip.style.overflow = 'visible';
    this.tooltip.textContent = '';
    this.overlay.style.display = 'block';
    this.overlay.style.position = 'static';
  }

  #onOverlayReady() {
    this.#initTooltipTextComponent();
    this.#overrideAttachOverlay();

    forwardAttrs(this, this.tooltip, {
      includeAttrs: ['position', 'opened'],
    });

    this.#handleTooltipVisibility();
  }

  #setTooltipTarget() {
    if (!this.children?.length) return;

    let ele = Array.from(this.children).find(
      (child) => child !== this.tooltip,
    );

    if (!ele) return;

    this.tooltip.target = ele;
  }

  #clearOverlayContentNode() {
    this.overlayContentNode.innerHTML = '';
  }

  #createEnrichedTextComponent() {
    const enrichedText = document.createElement('descope-enriched-text');

    enrichedText.setAttribute('link-target-blank', 'true');
    enrichedText.textContent = this.tooltipText;

    return enrichedText;
  }

  #initTooltipTextComponent() {
    if (!this.overlayContentNode) return;

    setTimeout(() => {
      this.#clearOverlayContentNode();

      this.textComponent = this.#createEnrichedTextComponent();

      this.overlayContentNode.appendChild(this.textComponent);

      forwardAttrs(this, this.textComponent, {
        includeAttrs: ['readonly'],
      });
    });
  }

  // the default vaadin behavior is to attach the overlay to the body when opened
  // we do not want that because it's difficult to style the overlay in this way
  // so we override it to open inside the shadow DOM
  #overrideAttachOverlay() {
    if (!this.overlay) return;

    if (this.isOpened) {
      // When `opened` attr is used, vaadin doesn't execute `_attachOverlay`,
      // and the overlay element is rendered outside the component, on the top
      // level. We need to move it back to the local component's DOM.
      setTimeout(() => {
        this.tooltip.shadowRoot.appendChild(this.overlay);
        this.#handleStaticDisplay();
      });
    } else {
      this.overlay._detachOverlay = () => {};

      this.overlay._attachOverlay = () =>
        this.tooltip.shadowRoot.appendChild(this.overlay);
    }
  }

  #handleTooltipVisibility() {
    // This is Vaadin's API for manual control of tooltip visibility
    this.tooltip?.toggleAttribute('manual', this.isOpened);
  }

  #updateText(value) {
    if (!this.textComponent) return;
    this.textComponent.textContent = value?.trim();
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (oldValue !== newValue) {
      if (attrName === 'text') {
        this.#updateText(newValue);
      }

      if (attrName === 'opened') {
        this.#handleTooltipVisibility(attrName, newValue)
      }
    }
  }
}

const { overlay, content } = {
  overlay: { selector: () => 'vaadin-tooltip-overlay::part(overlay)' },
  content: { selector: () => 'vaadin-tooltip-overlay::part(content)' },
};

/**
 * This component has no Shadow DOM of its own, so we can't add styles to it
 * (otherwise it would affect the rest of the DOM).
 * Note that all styles are within PortalMixin.
 */
export const TooltipClass = compose(
  componentNameValidationMixin,
  portalMixin({
    selector: '',
    mappings: {
      fontFamily: {
        ...content,
        property: EnrichedTextClass.cssVarList.fontFamilyOverride,
      },
      fontSize: {
        ...content,
        property: EnrichedTextClass.cssVarList.fontSizeOverride,
      },
      fontWeight: {
        ...content,
        property: EnrichedTextClass.cssVarList.fontWeightOverride,
      },
      textColor: {
        ...content,
        property: EnrichedTextClass.cssVarList.textColorOverride,
      },
      hostDirection: {
        ...content,
        property: EnrichedTextClass.cssVarList.hostDirectionOverride,
      },
      backgroundColor: [{ ...overlay }, { ...content }],
      borderColor: { ...overlay },
      borderStyle: { ...overlay },
      borderWidth: { ...overlay },
      borderRadius: { ...overlay },
      boxShadow: { ...overlay },
      horizontalPadding: [
        { ...content, property: 'padding-left' },
        { ...content, property: 'padding-right' },
      ],
      verticalPadding: [
        { ...content, property: 'padding-top' },
        { ...content, property: 'padding-bottom' },
      ],
    },
    forward: {
      attributes: ['style'],
      include: false,
    },
  }),
)(RawTooltip);
